import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Shield, 
  FileText, 
  Lock,
  Info,
  Calendar,
  Video,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalCompliancePanel = ({ selectedAG, token, onPublish, userRole }) => {
  const [legalStatus, setLegalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [visioInfo, setVisioInfo] = useState({
    modalites_connexion: '',
    modalites_vote: '',
    assistance_technique: ''
  });

  useEffect(() => {
    if (selectedAG?.id && token) {
      fetchLegalStatus();
    }
  }, [selectedAG?.id, token]);

  const fetchLegalStatus = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/legal-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLegalStatus(response.data);
    } catch (error) {
      console.error('Error fetching legal status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await axios.post(
        `${API}/assemblees/${selectedAG.id}/publish`,
        { visio_info: visioInfo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ ${response.data.message}\n\nDélai de préavis : ${response.data.days_notice} jours\nDate limite vote par correspondance : ${response.data.vote_deadline}`);
      setShowPublishModal(false);
      fetchLegalStatus();
      if (onPublish) onPublish();
    } catch (error) {
      alert(`❌ Erreur : ${error.response?.data?.detail || error.message}`);
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse flex items-center space-x-2">
          <div className="w-5 h-5 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-40"></div>
        </div>
      </div>
    );
  }

  if (!legalStatus) return null;

  const { legal_compliance, vote_correspondance, warnings, days_until_ag } = legalStatus;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
        <div className="flex items-center space-x-2">
          <Shield className="text-white" size={20} />
          <h3 className="font-semibold text-white">Conformité Légale</h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map((warning, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg flex items-start space-x-2 ${
                  warning.severity === 'error' ? 'bg-red-50 border border-red-200' :
                  warning.severity === 'warning' ? 'bg-amber-50 border border-amber-200' :
                  'bg-blue-50 border border-blue-200'
                }`}
              >
                <AlertTriangle 
                  size={18} 
                  className={`flex-shrink-0 mt-0.5 ${
                    warning.severity === 'error' ? 'text-red-600' :
                    warning.severity === 'warning' ? 'text-amber-600' :
                    'text-blue-600'
                  }`} 
                />
                <p className={`text-sm ${
                  warning.severity === 'error' ? 'text-red-700' :
                  warning.severity === 'warning' ? 'text-amber-700' :
                  'text-blue-700'
                }`}>
                  {warning.message}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Délai de convocation */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <Calendar size={18} className="text-gray-500" />
            <span className="text-sm text-gray-700">Délai avant AG</span>
          </div>
          <span className={`font-semibold ${days_until_ag >= 21 ? 'text-green-600' : 'text-red-600'}`}>
            {days_until_ag !== null ? `${days_until_ag} jours` : 'Non défini'}
            {days_until_ag >= 21 && <CheckCircle size={14} className="inline ml-1" />}
          </span>
        </div>

        {/* Statut convocation */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <FileText size={18} className="text-gray-500" />
            <span className="text-sm text-gray-700">Convocation</span>
          </div>
          {legal_compliance.convocation_sent ? (
            <span className="flex items-center text-green-600 font-medium text-sm">
              <CheckCircle size={14} className="mr-1" />
              Envoyée ({legal_compliance.days_notice}j de préavis)
            </span>
          ) : (
            <span className="text-amber-600 font-medium text-sm">Non envoyée</span>
          )}
        </div>

        {/* Paramètres verrouillés */}
        {legal_compliance.parameters_locked && (
          <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Lock size={18} className="text-amber-600" />
              <span className="text-sm text-amber-700">Paramètres verrouillés</span>
            </div>
            <span className="text-xs text-amber-600">
              Depuis le {new Date(legal_compliance.locked_at).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}

        {/* Vote par correspondance */}
        <div className={`p-3 rounded-lg ${vote_correspondance.deadline_passed ? 'bg-gray-100' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock size={18} className={vote_correspondance.deadline_passed ? 'text-gray-500' : 'text-green-600'} />
              <span className="text-sm font-medium">Vote par correspondance</span>
            </div>
            {vote_correspondance.deadline_passed ? (
              <span className="text-xs text-gray-500">Délai dépassé</span>
            ) : (
              <span className="text-xs text-green-600 font-medium">Ouvert</span>
            )}
          </div>
          {vote_correspondance.deadline && (
            <p className="text-xs text-gray-600 mt-1 ml-6">
              Date limite : {vote_correspondance.deadline}
            </p>
          )}
        </div>

        {/* Infos visio */}
        {legal_compliance.convocation_sent && (
          <div className={`p-3 rounded-lg ${legal_compliance.visio_info_included ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center space-x-2">
              <Video size={18} className={legal_compliance.visio_info_included ? 'text-green-600' : 'text-red-600'} />
              <span className="text-sm font-medium">Informations visioconférence</span>
              {legal_compliance.visio_info_included ? (
                <CheckCircle size={14} className="text-green-600" />
              ) : (
                <AlertTriangle size={14} className="text-red-600" />
              )}
            </div>
            {!legal_compliance.visio_info_included && (
              <p className="text-xs text-red-600 mt-1 ml-6">
                ⚠️ Risque juridique : modalités de connexion non précisées
              </p>
            )}
          </div>
        )}

        {/* Connexions loguées */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <Users size={18} className="text-gray-500" />
            <span className="text-sm text-gray-700">Connexions horodatées</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {legalStatus.connexions_count} évènement(s)
          </span>
        </div>

        {/* Bouton Publier */}
        {userRole === 'gestionnaire' && !legal_compliance.convocation_sent && (
          <button
            onClick={() => setShowPublishModal(true)}
            disabled={!legal_compliance.can_publish}
            className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
              legal_compliance.can_publish
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <FileText size={18} />
            <span>Envoyer la convocation</span>
          </button>
        )}

        {/* Info délai légal */}
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-1">Rappel réglementaire</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Convocation : 21 jours francs minimum</li>
                <li>Vote par correspondance : jusqu'à J-1 à 23h59</li>
                <li>Contestation : 2 mois après notification du PV</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Publier Convocation */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
              <FileText size={20} className="text-blue-600" />
              <span>Envoyer la convocation</span>
            </h3>
            
            <div className="space-y-4">
              {/* Avertissement */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <p className="font-medium">Action irréversible</p>
                    <p>Une fois envoyée, les paramètres de l'AG seront verrouillés et ne pourront plus être modifiés.</p>
                  </div>
                </div>
              </div>

              {/* Informations visioconférence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Video size={16} className="inline mr-1" />
                  Modalités de connexion *
                </label>
                <textarea
                  value={visioInfo.modalites_connexion}
                  onChange={(e) => setVisioInfo({...visioInfo, modalites_connexion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Ex: Connexion via le lien fourni dans l'espace coproprietaire..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modalités de vote à distance *
                </label>
                <textarea
                  value={visioInfo.modalites_vote}
                  onChange={(e) => setVisioInfo({...visioInfo, modalites_vote: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Ex: Vote électronique en temps réel via l'interface..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assistance technique
                </label>
                <textarea
                  value={visioInfo.assistance_technique}
                  onChange={(e) => setVisioInfo({...visioInfo, assistance_technique: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Ex: Support technique disponible au 01 XX XX XX XX..."
                />
              </div>

              {/* Info légale */}
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-medium mb-1">📋 Conformément à la loi :</p>
                <p>La convocation doit préciser le recours à la visioconférence, les modalités techniques de connexion, les modalités de vote à distance et l'assistance technique éventuelle.</p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowPublishModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing || !visioInfo.modalites_connexion || !visioInfo.modalites_vote}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {publishing ? 'Envoi...' : 'Confirmer l\'envoi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalCompliancePanel;
